import {
  FlowStatusEnum,
  getFlowFilters,
  getFlowProperties,
  IPerformanceReport,
  layoutQuery,
  localStorageKeyReportingPageFiltersValue,
  localStorageKeyReportingPageProperties,
  makeDynamicReportingPageStore,
  PageLayout,
  PageNames,
  parseDefaultQueryFilters,
  SyncFiltersPropertiesProvider,
  useEnvironment,
  useExportGridButton,
  useGetFiltersToolbarHeight,
  useLocationTrackingForPerformanceReport,
  usePerformancePageStore,
  useTrackRecentlyViewed,
  useWorkflows,
  HomePageItemDataType,
  useHasFeature,
  DianaFeaturesEnum,
  usePerformanceGraphQLExportParameters,
  mergeFiltersValues,
  useTableSort,
  useSidePeekDrawerProps,
  useEvents,
  Events,
  ErrorView,
  useFilteredFlowsByDefaultQueryFilters,
} from '@diana/core';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBreadcrumbsRoot } from '../../hooks/useBreadcrumbsRoot';
import { Content } from './Content';
import { FiltersToolbar } from './FiltersToolbar';
import { Header } from './Header';
import { createPropertyToFilter } from './Support';
import { uniqBy } from 'lodash';

export function PerformanceReportPage(props: { reportId: string }) {
  const { reportId } = props;
  const gridApiRef = useGridApiRef();
  const environment = useEnvironment();
  const syncFiltersWithUrl = useHasFeature(DianaFeaturesEnum.SyncFiltersWithUrl);
  const {
    isFetching: isFetchingFlows,
    data: flowsData,
    error,
  } = useWorkflows({
    reportId,
    flowStatuses: [FlowStatusEnum.Active, FlowStatusEnum.Archived],
  });

  const useReportingPageStore = makeDynamicReportingPageStore(reportId);
  const setFlows = useReportingPageStore((state) => state.setFlows);
  const setAvailableFilters = useReportingPageStore((state) => state.setAvailableFilters);
  const setDefaults = useReportingPageStore((state) => state.setDefaults);
  const setDefaultFiltersValues = useReportingPageStore((state) => state.setDefaultFiltersValues);
  const defaultFiltersValuesAndPropertiesSet = useReportingPageStore(
    (state) => state.defaultFiltersValuesAndPropertiesSet,
  );
  const availableFilters = useReportingPageStore((store) => store.availableFilters);
  const filtersValues = useReportingPageStore((store) => store.filtersValues);
  const defaultFiltersValues = useReportingPageStore((store) => store.defaultFiltersValues);
  const reports = usePerformancePageStore((state) => state.reports);
  const currentReport = reports.find((r) => r.id === reportId);
  const setReports = usePerformancePageStore((state) => state.setReports);

  const [isWideDrawer, setIsWideDrawer] = useState<boolean>(false);

  const sidePeekDrawerProps = useSidePeekDrawerProps({ isWideDrawer });
  const allFilterValues = mergeFiltersValues({
    selectedFiltersValues: filtersValues,
    defaultFiltersValues,
  });

  const layout = layoutQuery.getById({ id: reportId, displayName: currentReport?.displayName });
  const { data: layoutData, isFetching: isFetchingLayout } = useQuery({
    queryKey: layout.key,
    queryFn: layout.fn,
    enabled: Boolean(reportId),
  });

  useEffect(() => {
    if (!layoutData) return;
    setReports(uniqBy([...reports, layoutData as IPerformanceReport], 'id'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutData]);

  useTrackRecentlyViewed({ itemId: layoutData?.id, dataType: HomePageItemDataType.Report });

  const { track } = useLocationTrackingForPerformanceReport({ layout: layoutData || undefined });
  useEffect(() => {
    if (layoutData) track();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutData]);

  const propertyToFilter = useMemo(createPropertyToFilter, []);

  const flows = useFilteredFlowsByDefaultQueryFilters({
    flows: flowsData,
    defaultQueryFilters: layoutData?.defaultQueryFilters,
  });

  useEffect(() => {
    if (!flows || !flows.length) return;

    setFlows(flows);
    setAvailableFilters(
      getFlowFilters({
        flow: flows,
        propertyToFilter,
        onlyEnabled: false,
        onlyDefaultFilter: false,
        layoutData,
      }),
    );

    // eslint-disable-next-line
  }, [flows, setAvailableFilters, setFlows, layoutData]);

  useEvents((ctx) => {
    Events.ReportingPageItemOpened.attach(ctx, ({ isDashboardDrawer }) => {
      setIsWideDrawer(isDashboardDrawer);
    });
  }, []);

  const isDefaultFiltersValuesSet = useRef(false);
  useEffect(() => {
    if (
      flows &&
      flows.length &&
      layoutData?.defaultQueryFilters &&
      !isDefaultFiltersValuesSet.current
    ) {
      isDefaultFiltersValuesSet.current = true;
      setDefaultFiltersValues(parseDefaultQueryFilters(layoutData.defaultQueryFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutData?.defaultQueryFilters, flows]);

  const supportedProperties = useMemo(() => {
    return flows
      ? getFlowProperties({
          flow: flows,
          onlyEnabled: false,
          onlyDefaultFilter: false,
        })
      : [];
  }, [flows]);

  const { sortModel, onSortModelChange } = useTableSort({ supportedProperties });

  const queryParameters = usePerformanceGraphQLExportParameters({
    flows: flows ?? [],
    filters: availableFilters,
    filtersValues: allFilterValues,
    reportId,
    sortModel,
  });

  const { isExportModalOpen, setIsExportModalOpen, ExportModalComponent } = useExportGridButton({
    apiRef: gridApiRef,
    params: queryParameters,
    exportConfigurations: [],
  });

  const isContentLoading = isFetchingLayout || isFetchingFlows;
  const breadcrumbRoot = useBreadcrumbsRoot('performance');

  const height = useGetFiltersToolbarHeight(true);

  return (
    <SyncFiltersPropertiesProvider
      filtersKey={localStorageKeyReportingPageFiltersValue(environment, reportId)}
      propertiesKey={localStorageKeyReportingPageProperties(environment, reportId)}
      subscribe={useReportingPageStore.subscribe}
      storeSetDefaults={() => setDefaults(environment, reportId)}
      defaultFiltersValuesAndPropertiesSet={
        defaultFiltersValuesAndPropertiesSet || flows?.length === 0 || (!isFetchingFlows && !flows)
      }
    >
      <PageLayout
        name={PageNames.REPORTING}
        title={layoutData?.displayName || breadcrumbRoot.title}
        isContentLoading={isContentLoading}
        rightDrawer={sidePeekDrawerProps}
        filters={
          (!isFetchingFlows && !flows) || flows?.length === 0 ? undefined : (
            <FiltersToolbar
              syncFiltersWithUrl={syncFiltersWithUrl}
              reportId={reportId}
              layoutData={layoutData!}
              flows={flows!}
              needToShowManageDashboardButtons={layoutData?.showManageDashboardButtons !== false}
              isContentLoading={isContentLoading}
              height={height}
              gridApiRef={gridApiRef}
            />
          )
        }
        header={
          <Header
            reportId={reportId}
            flows={flows}
            companies={layoutData?.companies}
            layoutCompanyId={layoutData?.companyId}
            hasDifferentDataSource={!!layoutData?.dataSource}
            componentsLength={layoutData?.components.length}
            layoutDisplayName={layoutData?.displayName}
            setIsExportModalOpen={setIsExportModalOpen}
          />
        }
      >
        {error ? (
          <ErrorView responseError={error} />
        ) : (
          <Fragment>
            {!isContentLoading && layoutData && flows ? (
              <Content
                ref={gridApiRef}
                flows={flows}
                reportId={reportId}
                layoutData={layoutData}
                forceEmptySelection={isExportModalOpen}
                supportedProperties={supportedProperties || []}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
              />
            ) : null}
            {ExportModalComponent}
          </Fragment>
        )}
      </PageLayout>
    </SyncFiltersPropertiesProvider>
  );
}
